import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Icon, Spinner, Text, useDialog, useLocalizeMessage, useDateTimeFormat } from 'libs.nucleus';
import { ReportingCard } from 'libs.react';
import { ChartPreviewContent } from '../../../charts/components/chart_detail/chart_preview_content.component';
import { buildPivotConfigFromState, useChartQuery, useCharts } from '../../../charts/hooks';
import { getBuilderChartTypeFromData, toColumnConfig } from '../../../charts/utils';
export const DashboardChartCard = ({ chartId, chartName, studyId, environmentId, runCount, cubeConfigIds = [], filterParams = [], }) => {
    const translate = useLocalizeMessage();
    const dialog = useDialog();
    const { fetchChartById } = useCharts();
    const { formatDateTime } = useDateTimeFormat();
    const [chartData, setChartData] = useState(null);
    const [chartResource, setChartResource] = useState(null);
    const [liveChartName, setLiveChartName] = useState('');
    const [isLoadingChart, setIsLoadingChart] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const hasFetchedRef = useRef(false);
    // Snapshot study/env at the time of "Run" so changing selections doesn't trigger queries
    const [activeStudyId, setActiveStudyId] = useState('');
    const [activeEnvironmentId, setActiveEnvironmentId] = useState('');
    // Table chart: pagination and sort state (for TableChart component)
    const defaultPageSize = chartData?.defaultRowsPerPage ?? 10;
    const [tablePaginationState, setTablePaginationState] = useState({ pageIndex: 0, pageSize: defaultPageSize });
    const [tableSortingConfig, setTableSortingConfig] = useState({});
    // Build SettingsPanelState from chart data + snapshotted study/env + dashboard filters
    const settingsState = useMemo(() => {
        const chartFilters = chartData?.filters || [];
        const globalFilters = filterParams.map((fp) => ({
            member: fp.member,
            operator: fp.operator,
            values: fp.values,
            role: fp.role,
        }));
        const mergedFilters = [...chartFilters, ...globalFilters];
        return {
            chartType: getBuilderChartTypeFromData(chartData || undefined),
            chartTitle: chartData?.title || '',
            orientation: chartData?.type === 'horizontalBar' ? 'horizontal' : chartData?.orientation,
            showLabels: chartData?.showLabels,
            showLegend: chartData?.showLegend,
            studyId: activeStudyId,
            testEnvironmentId: activeEnvironmentId,
            columns: chartData?.columns,
            numberValues: chartData?.numberValues,
            xAxis: toColumnConfig(chartData?.xAxis),
            yAxis: toColumnConfig(chartData?.yAxis),
            splitBy: toColumnConfig(chartData?.splitBy, { role: 'dimension' }),
            xTitle: chartData?.xTitle,
            yTitle: chartData?.yTitle,
            labelOverrides: chartData?.labelOverrides,
            palette: chartData?.palette,
            conditionalFormats: chartData?.conditionalFormats,
            filters: mergedFilters.length > 0 ? mergedFilters : undefined,
            dimensionOrder: chartData?.dimensionOrder,
            measureOrder: chartData?.measureOrder,
            orderRules: chartData?.orderRules,
            defaultSort: Array.isArray(chartData?.defaultSort)
                ? chartData.defaultSort
                : chartData?.defaultSort
                    ? [chartData.defaultSort]
                    : undefined,
            defaultRowsPerPage: chartData?.defaultRowsPerPage,
            dotSizeMapping: chartData?.dotSizeMapping,
            dotSizeField: chartData?.dotSizeField,
            dotSizeConstant: chartData?.dotSizeConstant,
            normalizeBubbleSize: chartData?.normalizeBubbleSize,
            bubbleSizeMinOverride: chartData?.bubbleSizeMinOverride,
            bubbleSizeMaxOverride: chartData?.bubbleSizeMaxOverride,
            cardInfo: chartData?.cardInfo,
        };
    }, [chartData, activeStudyId, activeEnvironmentId, filterParams]);
    const { resultSet, isLoading: isQueryLoading, error: queryError, total, executeQuery, } = useChartQuery({
        state: settingsState,
        fallbackQuery: chartData?.query,
        chartResource,
        additionalCubeConfigIds: cubeConfigIds,
    });
    // Fetch chart config on mount
    useEffect(() => {
        if (hasFetchedRef.current) {
            return;
        }
        hasFetchedRef.current = true;
        const loadChart = async () => {
            try {
                const result = await fetchChartById(chartId);
                if (!result || !result.data) {
                    setLoadError(true);
                    return;
                }
                setChartData(result.data);
                setChartResource(result);
                setLiveChartName(result.data.title || result.name || '');
            }
            catch {
                setLoadError(true);
            }
            finally {
                setIsLoadingChart(false);
            }
        };
        loadChart();
    }, [chartId, fetchChartById]);
    // Keep a stable ref for executeQuery so effects don't re-trigger on function identity changes
    const executeQueryRef = useRef(executeQuery);
    executeQueryRef.current = executeQuery;
    // Snapshot study/env and execute query when "Run" is clicked
    const lastExecutedRunRef = useRef(0);
    const studyIdRef = useRef(studyId);
    studyIdRef.current = studyId;
    const environmentIdRef = useRef(environmentId);
    environmentIdRef.current = environmentId;
    useEffect(() => {
        if (runCount > 0 && chartData && runCount !== lastExecutedRunRef.current) {
            lastExecutedRunRef.current = runCount;
            setActiveStudyId(studyIdRef.current);
            setActiveEnvironmentId(environmentIdRef.current);
            setTablePaginationState({ pageIndex: 0, pageSize: chartData?.defaultRowsPerPage ?? 10 });
            setTableSortingConfig({});
        }
    }, [runCount, chartData]);
    // Execute the query after activeStudyId/activeEnvironmentId are set (triggered by Run)
    useEffect(() => {
        if (activeStudyId && activeEnvironmentId && lastExecutedRunRef.current > 0) {
            executeQueryRef.current();
            setLastRefreshed(new Date());
        }
    }, [activeStudyId, activeEnvironmentId]);
    // Re-execute query when dashboard-level global filters change (after initial run)
    const prevFilterParamsRef = useRef(filterParams);
    useEffect(() => {
        if (prevFilterParamsRef.current === filterParams) {
            return;
        }
        prevFilterParamsRef.current = filterParams;
        if (lastExecutedRunRef.current > 0 && chartData) {
            executeQueryRef.current();
            setLastRefreshed(new Date());
        }
    }, [filterParams, chartData]);
    // Handle table page changes
    const handlePageChange = useCallback((pageIndex, pageSize) => {
        setTablePaginationState({ pageIndex, pageSize });
        executeQuery({ pageIndex, pageSize, order: tableSortingConfig });
    }, [executeQuery, tableSortingConfig]);
    // Handle table sort changes
    const handleSortChange = useCallback((config) => {
        setTableSortingConfig(config);
        setTablePaginationState((prev) => ({ ...prev, pageIndex: 0 }));
        executeQuery({
            pageIndex: 0,
            pageSize: tablePaginationState.pageSize,
            order: config,
        });
    }, [executeQuery, tablePaginationState.pageSize]);
    // ── Kebab menu: info card action ──────────────────────────────────────────
    const handleShowInfoCard = useCallback(async () => {
        if (!chartData?.cardInfo) {
            return;
        }
        await dialog.open({
            title: translate('Information card'),
            body: _jsx("div", { className: 'max-h-[500px] overflow-auto', dangerouslySetInnerHTML: { __html: chartData.cardInfo } }),
            actionButtons: [{ label: translate('Close') }],
        });
    }, [chartData?.cardInfo, dialog, translate]);
    // ── Kebab menu items ──────────────────────────────────────────────────────
    const kebabItems = useMemo(() => [
        {
            id: `last-refresh-${chartId}`,
            content: lastRefreshed
                ? `${translate('Last refreshed')}: ${formatDateTime(lastRefreshed)}`
                : translate('Not yet refreshed'),
            onClick: () => { },
            disabled: true,
        },
        {
            id: `info-${chartId}`,
            content: translate('See info card'),
            onClick: handleShowInfoCard,
            hidden: !chartData?.cardInfo,
        },
    ], [chartId, translate, handleShowInfoCard, chartData?.cardInfo, lastRefreshed, formatDateTime]);
    // ── Card content ──────────────────────────────────────────────────────────
    const renderContent = () => {
        if (isLoadingChart) {
            return (_jsx("div", { className: 'flex items-center justify-center size-full', children: _jsx(Spinner, {}) }));
        }
        if (loadError) {
            return (_jsxs("div", { className: 'flex flex-col items-center justify-center size-full gap-2', children: [_jsx(Icon, { name: 'warning', size: 'base' }), _jsx(Text, { size: 'sm', className: 'text-neutral-400', children: translate('This chart is no longer available') })] }));
        }
        if (runCount === 0) {
            return (_jsx("div", { className: 'flex items-center justify-center size-full', children: _jsx(Text, { size: 'sm', className: 'text-neutral-400', children: translate('Click Run to load data') }) }));
        }
        if (!chartData) {
            return (_jsx("div", { className: 'flex items-center justify-center size-full', children: _jsx(Spinner, {}) }));
        }
        return (_jsx(ChartPreviewContent, { chartType: settingsState.chartType, resultSet: resultSet, isLoading: isQueryLoading, error: queryError, total: total, onPageChange: handlePageChange, tablePaginationState: tablePaginationState, setTableSortingConfig: handleSortChange, labelOverrides: settingsState.labelOverrides, conditionalFormats: settingsState.conditionalFormats, xAxis: settingsState.xAxis, yAxis: settingsState.yAxis, splitBy: settingsState.splitBy, orientation: settingsState.orientation, pivotConfig: buildPivotConfigFromState(settingsState) || chartData?.pivotConfig, palette: settingsState.palette, xTitle: settingsState.xTitle, yTitle: settingsState.yTitle, showLabels: settingsState.showLabels, showLegend: settingsState.showLegend, numberValues: settingsState.numberValues, dotSizeMapping: settingsState.dotSizeMapping, dotSizeField: settingsState.dotSizeField, dotSizeConstant: settingsState.dotSizeConstant, normalizeBubbleSize: settingsState.normalizeBubbleSize, bubbleSizeMinOverride: settingsState.bubbleSizeMinOverride, bubbleSizeMaxOverride: settingsState.bubbleSizeMaxOverride, defaultSortedColumns: settingsState.defaultSort }));
    };
    return (_jsx(ReportingCard, { title: _jsx("div", { className: 'px-2', children: liveChartName || chartName }), headerAction: _jsx(Dropdown, { variant: 'kebab', position: 'bottom-end', items: kebabItems }), height: 'full', className: 'h-full', children: renderContent() }));
};
